import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  makeStyles,
  createStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { userProfileApiRef } from '../../../apis/userProfileApi';
import UserAvatar from '../UserAvatar';
import {
  extractNameFromEmail,
  filterReviewsByTime,
  FilterPeriod,
  formatTimeAgo,
  UserReview,
} from './ratingTypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    filterContainer: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      padding: '0 0 16px 0',
      justifyContent: 'flex-start',
      width: '100%',
      alignItems: 'flex-start',
      flexShrink: 0,
    },
    filterButton: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '13px',
      padding: '6px 12px',
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    filterButtonActive: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      border: `1px solid ${theme.palette.primary.main}`,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    scrollArea: {
      flex: 1,
      overflowY: 'auto',
      minHeight: 0,
    },
    reviewCard: {
      marginBottom: '12px',
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        marginBottom: '0',
      },
    },
    reviewCardContent: {
      padding: '12px 16px',
      '&:last-child': {
        paddingBottom: '12px',
      },
      display: 'flex',
      gap: '12px',
    },
    reviewCardAvatar: {
      flexShrink: 0,
      width: '36px',
      height: '36px',
    },
    reviewCardMain: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    reviewHeaderTop: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between',
      width: '100%',
    },
    userName: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: '13px',
    },
    userApm: {
      fontSize: '11px',
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.action.hover,
      padding: '2px 8px',
      borderRadius: '12px',
      display: 'inline-block',
      marginLeft: '8px',
    },
    timestamp: {
      color: theme.palette.text.secondary,
      fontSize: '11px',
      whiteSpace: 'nowrap',
    },
    reviewText: {
      fontSize: '13px',
      color: theme.palette.text.secondary,
      lineHeight: '1.5',
      wordWrap: 'break-word',
      width: 'auto',
      maxWidth: '345px',
    },
    reviewTextCollapsed: {
      fontSize: '13px',
      color: theme.palette.text.secondary,
      lineHeight: '1.5',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      userSelect: 'none',
      width: 'auto',
      maxWidth: '345px',
      '&:hover': {
        opacity: 0.8,
      },
    },
    emptyState: {
      textAlign: 'center',
      padding: '32px 16px',
      color: theme.palette.text.secondary,
    },
  }),
);

interface RatingListProps {
  reviews: UserReview[];
  showFilters?: boolean;
}

export const RatingList: React.FC<RatingListProps> = ({
  reviews,
  showFilters = true,
}) => {
  const classes = useStyles();
  const userProfileApi = useApi(userProfileApiRef);
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
  const [expandedReviews, setExpandedReviews] = useState<Set<number>>(
    new Set(),
  );

  const sortedReviews = [...(reviews ?? [])].reverse();
  const filteredReviews = filterReviewsByTime(sortedReviews, filterPeriod);

  const toggleReviewExpansion = (index: number) => {
    const next = new Set(expandedReviews);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setExpandedReviews(next);
  };

  const FILTER_OPTIONS: { value: FilterPeriod; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: '3m', label: 'Last 3 months' },
    { value: '6m', label: 'Last 6 months' },
    { value: '1y', label: 'Last 1 year' },
  ];

  const renderReviewCard = (review: UserReview, index: number) => (
    <Card key={index} className={classes.reviewCard}>
      <CardContent className={classes.reviewCardContent}>
        <div style={{ display: 'inline-flex' }}>
          <UserAvatar
            userEmail={review.userEmail}
            customStyles={{
              width: '36px',
              height: '36px',
              fontSize: '14px',
            }}
          />
        </div>
        <Box className={classes.reviewCardMain}>
          <Box className={classes.reviewHeaderTop}>
            <Box>
              <span className={classes.userName}>
                {review.userEmail
                  ? extractNameFromEmail(review.userEmail)
                  : 'Anonymous'}
              </span>
              {review.apmNumber && (
                <span className={classes.userApm}>{review.apmNumber}</span>
              )}
            </Box>
            <span className={classes.timestamp}>
              {formatTimeAgo(review.timestamp)}
            </span>
          </Box>
          <Rating value={review.rating} readOnly size="small" />
          {review.comment && (
            <Typography
              className={
                expandedReviews.has(index)
                  ? classes.reviewText
                  : classes.reviewTextCollapsed
              }
              onClick={() => toggleReviewExpansion(index)}
            >
              {review.comment}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box className={classes.root}>
      {showFilters && (
        <Box className={classes.filterContainer}>
          {FILTER_OPTIONS.map(opt => (
            <Button
              key={opt.value}
              size="small"
              variant="outlined"
              className={
                filterPeriod === opt.value
                  ? `${classes.filterButton} ${classes.filterButtonActive}`
                  : classes.filterButton
              }
              onClick={() => setFilterPeriod(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </Box>
      )}

      <Box className={classes.scrollArea}>
        {filteredReviews.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography>
              {filterPeriod === 'all'
                ? 'No reviews yet'
                : "Hey! Hope you get to be the first one to check this out. Let's do this!"}
            </Typography>
          </Box>
        ) : (
          filteredReviews.map((review, index) =>
            renderReviewCard(review, index),
          )
        )}
      </Box>
    </Box>
  );
};
